import React, { FC, useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  GetMemoriesResponse,
  MemoryItem,
  CredentialItem,
  AgentEvents,
  MemoryUpdatedEvent,
} from '@dreamai/types';
import api from '../../api';
import { usePusher } from '../../contexts/PusherContext';
import DreamMarque from '../common/DreamMarque';
import { ReactComponent as DreamWordmark } from '../../icons/brand/dream-wordmark.svg';
import { ReactComponent as VisibleIcon } from '../../icons/visible.svg';
import { ReactComponent as NotVisibleIcon } from '../../icons/not-visible.svg';
import MemoryCard from './MemoryCard';
import CredentialCard from './CredentialCard';
import MemoryEmptyState from './MemoryEmptyState';
import { OnboardingModal } from '../onboarding/OnboardingModal';
import AddPaymentCardModal from './sidebar/AddPaymentCardModal';
import SecureCredentialModal, {
  SecureCredentialSubmitOptions,
} from './sidebar/SecureCredentialModal';
import {
  SecureCredentialSubmitResult,
  submitErrorFromUnknown,
} from './sidebar/credentialSubmitResult';
import './memory-area.scss';

type TabType =
  | 'unconfirmed'
  | 'aboutYou'
  | 'organisation'
  | 'relationships'
  | 'credentials';

interface MemoryAreaProps {
  /** Rendered inside a host that supplies its own chrome (top nav, back, title). */
  embedded?: boolean;
}

const MemoryArea: FC<MemoryAreaProps> = ({ embedded = false }) => {
  const { getAccessTokenSilently } = useAuth0();
  const rootClassName = embedded
    ? 'memory-area memory-area--embedded'
    : 'memory-area inset-white-backed';
  const pusherChannel = usePusher();

  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [items, setItems] = useState<MemoryItem[]>([]);
  const [credentials, setCredentials] = useState<CredentialItem[]>([]);
  const [counts, setCounts] = useState<GetMemoriesResponse['counts'] | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState<TabType>('unconfirmed');

  // Filters for unconfirmed tab
  const [filterAboutYou, setFilterAboutYou] = useState(true);
  const [filterOrganisation, setFilterOrganisation] = useState(true);
  const [filterRelationships, setFilterRelationships] = useState(true);

  // Add-credential modals for the Credentials tab. Users land here to
  // review what Dream has stored on their behalf — we also want them to
  // be able to pro-actively add a sign-in or a card before an assistant
  // ever asks for one.
  const [showAddCardModal, setShowAddCardModal] = useState(false);
  const [showAddLoginModal, setShowAddLoginModal] = useState(false);
  const [addLoginSubmitting, setAddLoginSubmitting] = useState(false);

  const fetchMemories = useCallback(
    async (isInitial = false) => {
      if (isInitial) setInitialLoading(true);
      setError(null);

      try {
        const token = await getAccessTokenSilently();
        const response = await api.get<GetMemoriesResponse>('/memories', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.success) {
          setItems(response.data.items);
          setCredentials(response.data.credentials || []);
          setCounts(response.data.counts);

          // Default to unconfirmed tab if there are unconfirmed items
          if (response.data.counts.unconfirmed > 0) {
            setActiveTab('unconfirmed');
          } else if (response.data.counts.aboutYou > 0) {
            setActiveTab('aboutYou');
          } else if (response.data.counts.organisation > 0) {
            setActiveTab('organisation');
          } else if (response.data.counts.relationships > 0) {
            setActiveTab('relationships');
          }
        } else {
          setError('Failed to load memories');
        }
      } catch (err) {
        console.error('Error fetching memories:', err);
        setError('Failed to load memories');
      } finally {
        if (isInitial) setInitialLoading(false);
      }
    },
    [getAccessTokenSilently],
  );

  // Initial fetch
  useEffect(() => {
    fetchMemories(true);
  }, [fetchMemories]);

  // Listen for Pusher events for memory updates
  useEffect(() => {
    if (!pusherChannel) return;

    const handleMemoryUpdated = (data: MemoryUpdatedEvent) => {
      fetchMemories();
    };

    pusherChannel.bind(AgentEvents.MEMORY_UPDATED, handleMemoryUpdated);

    return () => {
      pusherChannel.unbind(AgentEvents.MEMORY_UPDATED, handleMemoryUpdated);
    };
  }, [pusherChannel, fetchMemories]);

  const handleApprove = async (item: MemoryItem) => {
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/memories/approve',
        { id: item.id, type: item.type },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      // Refresh will happen via Pusher
    } catch (err) {
      console.error('Error approving memory:', err);
    }
  };

  const handleDelete = async (item: MemoryItem) => {
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/memories/delete',
        { id: item.id, type: item.type },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      // Refresh will happen via Pusher
    } catch (err) {
      console.error('Error deleting memory:', err);
    }
  };

  // Persist a user-initiated sign-in credential from the Memories tab.
  // Same endpoint as every other credential-entry surface — the modal
  // now hands us a flat `{values}` map from its dynamic field editor,
  // plus an `opts` bag with the top-level metadata (label / site /
  // purpose) and the explicit `secretFieldKeys` for the BE secret-count
  // gate. Provenance is `user_form` via the BE default.
  const handleAddLoginSubmit = async (
    values: Record<string, string>,
    opts: SecureCredentialSubmitOptions,
  ): Promise<SecureCredentialSubmitResult> => {
    if (addLoginSubmitting) {
      return { ok: false, error: { message: 'Already saving.' } };
    }
    const site = (opts.site || '').trim();
    // Derive a reasonable default label when the user didn't pick one:
    // site → first identifier-shaped value → "Sign-in".
    const userLabel = (opts.label || '').trim();
    const fallbackLabel =
      site ||
      values.username?.trim() ||
      values.email?.trim() ||
      values.login?.trim() ||
      'Sign-in';
    const label = userLabel || fallbackLabel;
    setAddLoginSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await api.post(
        '/memories/store-credential',
        {
          label,
          site: site || undefined,
          credentialType: 'login',
          fields: values,
          secretFieldKeys: opts.secretFieldKeys,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data?.success) {
        fetchMemories();
        return { ok: true };
      }
      return {
        ok: false,
        error: {
          code:
            typeof response.data?.code === 'string'
              ? response.data.code
              : undefined,
          message: response.data?.error || 'Failed to save sign-in details.',
        },
      };
    } catch (err) {
      console.error('Error adding sign-in credential:', err);
      return { ok: false, error: submitErrorFromUnknown(err) };
    } finally {
      setAddLoginSubmitting(false);
    }
  };

  const handleInvalidateCredential = async (credentialId: string) => {
    try {
      const token = await getAccessTokenSilently();
      await api.post(
        '/memories/invalidate-credential',
        { credentialId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      // Refresh will happen via Pusher
    } catch (err) {
      console.error('Error invalidating credential:', err);
    }
  };

  // Filter items based on active tab and filters
  const getFilteredItems = (): MemoryItem[] => {
    if (activeTab === 'unconfirmed') {
      return items.filter((item) => {
        if (item.approved) return false;
        if (item.scope === 'aboutYou' && !filterAboutYou) return false;
        if (item.scope === 'organisation' && !filterOrganisation) return false;
        if (item.scope === 'relationships' && !filterRelationships)
          return false;
        return true;
      });
    }

    return items.filter((item) => item.scope === activeTab && item.approved);
  };

  const filteredItems = getFilteredItems();

  if (initialLoading) {
    return (
      <div className={rootClassName}>
        <div className='memory-skeleton'>
          <div className='memory-skeleton-header'>
            <div className='memory-skeleton-title' />
            <div className='memory-skeleton-subtitle' />
          </div>
          <div className='memory-skeleton-tabs'>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className='memory-skeleton-tab' />
            ))}
          </div>
          <div className='memory-skeleton-cards'>
            {[1, 2, 3].map((i) => (
              <div key={i} className='memory-skeleton-card'>
                <div className='memory-skeleton-card-tag' />
                <div className='memory-skeleton-card-line long' />
                <div className='memory-skeleton-card-line medium' />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={rootClassName}>
        <div className='error'>{error}</div>
      </div>
    );
  }

  return (
    <div className={rootClassName}>
      {!embedded && (
        <div className='memory-top-bar'>
          <Link to='/' className='memory-top-bar-logo' aria-label='Home'>
            <DreamMarque className='memory-top-bar-marque' />
            <DreamWordmark className='memory-top-bar-wordmark' />
          </Link>
        </div>
      )}
      <OnboardingModal
        modalKey='memory_page_intro'
        title='Your Dreamies remember!'
        imageSrc='/onboarding-modals/memory-page-intro.jpg'
        imageAlt=''
        primaryButtonText='Explore my memories'
        className='memory-intro-modal'
      >
        <p>
          This page shows you everything that your secretary remembers about
          you, where you work, and the people you know.
        </p>
        <p>
          Your secretary will learn about you passively from emails, calendar,
          and other activities. You can choose to delete any memory they created
          on this page.
        </p>
      </OnboardingModal>

      {!embedded && (
        <div className='header'>
          <Link to='/' className='memory-back-link'>
            <svg width='20' height='20' viewBox='0 0 20 20' fill='none'>
              <path
                d='M16.6668 10H3.3335M3.3335 10L10.0002 16.6667M3.3335 10L10.0002 3.33333'
                stroke='currentColor'
                strokeWidth='1.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </svg>
            <span>Back to Home</span>
          </Link>
          <div className='header-text'>
            <h2>Memories</h2>
            <p>Manage the memories and saved credentials your secretary uses</p>
          </div>
        </div>
      )}

      <div className='tabs'>
        <div
          className={`tab ${activeTab === 'unconfirmed' ? 'active' : ''}`}
          onClick={() => setActiveTab('unconfirmed')}
        >
          Unconfirmed memories{' '}
          <span
            className={`count ${(counts?.unconfirmed || 0) > 0 ? 'urgent' : ''}`}
          >
            {counts?.unconfirmed || 0}
          </span>
        </div>
        <div
          className={`tab ${activeTab === 'aboutYou' ? 'active' : ''}`}
          onClick={() => setActiveTab('aboutYou')}
        >
          About you{' '}
          <span className='count'>{counts?.aboutYouApproved || 0}</span>
        </div>
        <div
          className={`tab ${activeTab === 'organisation' ? 'active' : ''}`}
          onClick={() => setActiveTab('organisation')}
        >
          Your organisation{' '}
          <span className='count'>{counts?.organisationApproved || 0}</span>
        </div>
        <div
          className={`tab ${activeTab === 'relationships' ? 'active' : ''}`}
          onClick={() => setActiveTab('relationships')}
        >
          People you know{' '}
          <span className='count'>{counts?.relationshipsApproved || 0}</span>
        </div>
        <div
          className={`tab ${activeTab === 'credentials' ? 'active' : ''}`}
          onClick={() => setActiveTab('credentials')}
        >
          Credentials <span className='count'>{counts?.credentials || 0}</span>
        </div>
      </div>

      <div className='content'>
        <div className='content-inner'>
          {activeTab === 'unconfirmed' && counts && counts.unconfirmed > 0 && (
            <>
              <p className='explanation'>
                Until they're confirmed, your dreamies won't use the memory or
                recognise the relationship.
              </p>

              <h2 className='section-title'>Your team wants you to confirm</h2>

              <div className='filters'>
                <button
                  className={`filter-btn ${filterAboutYou ? 'active' : ''}`}
                  onClick={() => setFilterAboutYou(!filterAboutYou)}
                >
                  <span className='count'>
                    {counts?.aboutYouUnconfirmed || 0}
                  </span>
                  About you
                  {filterAboutYou ? <VisibleIcon /> : <NotVisibleIcon />}
                </button>
                <button
                  className={`filter-btn ${filterOrganisation ? 'active' : ''}`}
                  onClick={() => setFilterOrganisation(!filterOrganisation)}
                >
                  <span className='count'>
                    {counts?.organisationUnconfirmed || 0}
                  </span>
                  Your organisation
                  {filterOrganisation ? <VisibleIcon /> : <NotVisibleIcon />}
                </button>
                <button
                  className={`filter-btn ${filterRelationships ? 'active' : ''}`}
                  onClick={() => setFilterRelationships(!filterRelationships)}
                >
                  <span className='count'>
                    {counts?.relationshipsUnconfirmed || 0}
                  </span>
                  People you know
                  {filterRelationships ? <VisibleIcon /> : <NotVisibleIcon />}
                </button>
              </div>
            </>
          )}

          {activeTab === 'credentials' ? (
            <>
              <div className='credentials-add-actions'>
                <button
                  type='button'
                  className='credentials-add-btn'
                  onClick={() => setShowAddLoginModal(true)}
                >
                  + Add sign-in details
                </button>
                <button
                  type='button'
                  className='credentials-add-btn'
                  onClick={() => setShowAddCardModal(true)}
                >
                  + Add payment card
                </button>
              </div>
              {credentials.length === 0 ? (
                <MemoryEmptyState variant='credentials' />
              ) : (
                <div className='items'>
                  {credentials.map((cred) => (
                    <CredentialCard
                      key={cred.id}
                      credential={cred}
                      onInvalidate={handleInvalidateCredential}
                      onUpdated={() => fetchMemories()}
                    />
                  ))}
                </div>
              )}
            </>
          ) : filteredItems.length === 0 ? (
            <MemoryEmptyState variant={activeTab} />
          ) : (
            <div className='items'>
              {filteredItems.map((item) => (
                <MemoryCard
                  key={item.id}
                  item={item}
                  onApprove={handleApprove}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <AddPaymentCardModal
        show={showAddCardModal}
        onClose={() => setShowAddCardModal(false)}
        onSuccess={() => {
          setShowAddCardModal(false);
          fetchMemories();
        }}
      />

      {showAddLoginModal && (
        // User-initiated add — no `formFields`, no fixed label, no fixed
        // site. The modal renders its own Label and Site inputs at the
        // top, seeds the editor with [username, password] (the common
        // case), and lets the user add as many extra rows as they need
        // (PIN, security answer, recovery code…). Any combination is
        // valid as long as at least one row is marked Secret.
        <SecureCredentialModal
          title='Add sign-in details'
          overview='Save a sign-in to your secure vault. Your secretary can use it to sign in on your behalf when they need to. At least one field must be marked Secret.'
          submitLabel='Save sign-in'
          onSubmit={handleAddLoginSubmit}
          onClose={() => setShowAddLoginModal(false)}
          submitting={addLoginSubmitting}
        />
      )}
    </div>
  );
};

export default MemoryArea;
